import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

/** Entitlement snapshot shared across pages via a lightweight event bus. */
const SUB_EVENT = "mc:subscription-changed";

let cache = null;
let cachePromise = null;
let expiryTimer = null;

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * BUSINESS RULE — Upgrade UI visibility in navigation
 * ─────────────────────────────────────────────────────────────────────────────
 * While the user is inside their active 7-day free trial we intentionally
 * HIDE every Upgrade-related affordance from navigation surfaces (desktop
 * sidebar, mobile hamburger drawer, any shared nav component). The trial
 * already gives full access to every feature — nagging the user with an
 * "Upgrade" badge or CTA is redundant and hurts the onboarding experience.
 *
 * Once the trial expires we automatically restore the Upgrade UI (no refresh
 * required — see the `scheduleTrialExpiryRefresh` timer below and the
 * `visibilitychange` listener, both of which call `invalidateEntitlements`
 * when the trial crosses the expiry boundary).
 *
 *   trial_active  === true  → Upgrade UI hidden
 *   trial_expired === true  → Upgrade UI visible (paid users are never nagged)
 *
 * Rule of thumb for future contributors: any new "Upgrade / Actualizar / Ver
 * planes / plan badge" element you plan to render inside a NAVIGATION surface
 * MUST be gated with `showsUpgradeUI(status)` (or the shortcut
 * `useShowUpgradeUI()`). Do not gate content pages with this — page-level
 * upgrade prompts (FeatureGate, UpgradeModal, PaywallModal) have their own
 * flow and are already ignored during trial because `has()` returns true.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function showsUpgradeUI(status) {
  if (!status) return false; // fail-closed while loading — avoids flashing badges
  if (status.trial_active) return false;
  if (status.is_paid) return false;
  return true;
}

/** Schedule an automatic refresh at the exact moment the trial expires so the
 *  UI can react without waiting for a page reload. Safe to call multiple times —
 *  it clears any previously scheduled timer. */
function scheduleTrialExpiryRefresh(status) {
  if (expiryTimer) { clearTimeout(expiryTimer); expiryTimer = null; }
  if (!status || !status.trial_active || !status.trial_end) return;
  const end = Date.parse(status.trial_end);
  if (Number.isNaN(end)) return;
  const delta = end - Date.now();
  // Ignore anything in the past (< 0) — a status refresh will pick it up.
  // Cap the delta at 24h to keep setTimeout robust to clock skew / long trials.
  if (delta <= 0 || delta > 24 * 60 * 60 * 1000) return;
  expiryTimer = setTimeout(() => { invalidateEntitlements(); }, delta + 500);
}

export function useEntitlements() {
  const [status, setStatus] = useState(cache);
  const [loading, setLoading] = useState(!cache);

  const load = useCallback(async () => {
    if (!cachePromise) {
      cachePromise = api.get("/subscription/status", { withCredentials: true })
        .then((r) => { cache = r.data; return r.data; })
        .catch(() => { cache = null; return null; })
        .finally(() => { cachePromise = null; });
    }
    const data = await cachePromise;
    setStatus(data);
    setLoading(false);
    scheduleTrialExpiryRefresh(data);
    return data;
  }, []);

  const refresh = useCallback(async () => {
    cache = null; cachePromise = null;
    const data = await load();
    window.dispatchEvent(new CustomEvent(SUB_EVENT, { detail: data }));
    return data;
  }, [load]);

  useEffect(() => {
    load();
    const onChange = (e) => setStatus(e.detail);
    // If the user tabs back in we re-check — cheap safety net for the case
    // where the trial expired while the tab was in the background.
    const onVisible = () => { if (!document.hidden) refresh(); };
    window.addEventListener(SUB_EVENT, onChange);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener(SUB_EVENT, onChange);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load, refresh]);

  const has = useCallback((featureKey) => {
    if (!status) return true; // fail-open while loading — avoids flashing lock UI
    return Boolean(status.features?.[featureKey]);
  }, [status]);

  return { status, loading, has, refresh, showUpgradeUI: showsUpgradeUI(status) };
}

/** Convenience hook — returns a single boolean for components that only care
 *  whether Upgrade badges/buttons/CTAs should be rendered right now. */
export function useShowUpgradeUI() {
  const { showUpgradeUI } = useEntitlements();
  return showUpgradeUI;
}

export function invalidateEntitlements() {
  cache = null; cachePromise = null;
  if (expiryTimer) { clearTimeout(expiryTimer); expiryTimer = null; }
  window.dispatchEvent(new CustomEvent(SUB_EVENT));
}
